import { startCase } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { SnPasswordAPI } from '@lib/api/services';
import { TranslationService } from '@lib/translation/services/translation.service';

@Component({
  selector: 'sn-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup;
  public isLoading = false;
  public componentReady = false;
  public hideNewPassword = true;
  public hideConfirmPassword = true;
  public translationsLoaded = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _passwordAPI: SnPasswordAPI,
    private readonly _toastrService: ToastrService,
    private readonly _translationService: TranslationService,
  ) {
    // Initialize form immediately
    this.form = new UntypedFormGroup({
      authenticationCode: new UntypedFormControl('', [Validators.required]),
      newPassword: new UntypedFormControl('', [Validators.required, Validators.minLength(12)]),
      confirmPassword: new UntypedFormControl('', [
        Validators.required,
        this.createCompareValidator('newPassword'),
      ]),
    });

    setTimeout(() => {
      this.componentReady = true;
      this._changeDetectorRef.markForCheck();
    }, 0);
  }

  ngOnInit(): void {
    this._translationService.translationReady$().pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.translationsLoaded = true;
      this._changeDetectorRef.detectChanges();
    });

    const { uid, token } = this._activatedRoute.snapshot.params;

    if (!uid || !token) {
      this._toastrService.error('Invalid password reset link. Please request a new one.');
      this._router.navigate(['/auth', 'sign-in']);
    }
  }

  createCompareValidator(fieldToCompare: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent || !control.parent.get(fieldToCompare)) {
        return null;
      }

      return control.value === control.parent.get(fieldToCompare).value
        ? null
        : { passwordMismatch: true };
    };
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key).markAsTouched();
      });
      return;
    }

    this.isLoading = true;
    this._changeDetectorRef.markForCheck();

    const { uid, token } = this._activatedRoute.snapshot.params;

    if (!uid || !token) {
      this._toastrService.error('Invalid password reset link. Please request a new one.');
      this.isLoading = false;
      this._changeDetectorRef.markForCheck();
      return;
    }

    const payload = {
      uid,
      token,
      authenticationCode: this.form.get('authenticationCode').value,
      newPassword: this.form.get('newPassword').value,
    };

    this._passwordAPI
      .resetPasswordConfirm(payload)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe({
        next: this._onSuccess.bind(this),
        error: this._onError.bind(this),
      });
  }

  public toStartCase(text: string): string {
    return startCase(text);
  }

  private _onSuccess({ email }: any): void {
    this._toastrService.success('Your password has been reset successfully');
    this._router.navigate(['/auth', 'sign-in'], {
      queryParams: { email },
    });
  }

  private _onError(errorResponse: HttpErrorResponse): void {
    this.form.setErrors(errorResponse.error);
  }
}
